import React from 'react';
import { COLORS } from '../constants';

export type InfoType = 'about' | 'contact' | 'terms' | 'privacy' | null;

interface InfoModalProps {
  type: InfoType;
  onClose: () => void;
}

const InfoModal: React.FC<InfoModalProps> = ({ type, onClose }) => {
  if (!type) return null;

  const content = {
    about: {
      title: 'About Novus Energy',
      body: (
        <div className="space-y-4">
          <p>Founded in 2015, Novus Energy is dedicated to accelerating the world's transition to sustainable energy. We specialize in high-performance solar and wind infrastructure that empowers both businesses and homeowners.</p>
          <p>Our mission is to provide reliable, efficient, and accessible renewable energy solutions globally. With a team of over 120,000 professionals, we are building the foundation for a zero-pollution future.</p>
          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="bg-gray-50 p-4 rounded-xl">
              <p className="text-xs font-bold text-emerald-500 uppercase">Our Vision</p>
              <p className="text-sm font-medium">A world powered entirely by clean, renewable resources.</p>
            </div>
            <div className="bg-gray-50 p-4 rounded-xl">
              <p className="text-xs font-bold text-emerald-500 uppercase">Our Reach</p>
              <p className="text-sm font-medium">Active projects across 45 countries and 6 continents.</p>
            </div>
          </div>
        </div>
      )
    },
    contact: {
      title: 'Contact Information',
      body: (
        <div className="space-y-6">
          <p>Get in touch with our experts to learn how we can help you transition to sustainable energy.</p>
          <div className="space-y-4">
            <div className="flex items-center gap-4 bg-gray-50 p-4 rounded-xl">
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-emerald-500 shadow-sm">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase">Email Us</p>
                <p className="font-bold">support@novus.energy</p>
              </div>
            </div>
            <div className="flex items-center gap-4 bg-gray-50 p-4 rounded-xl">
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-emerald-500 shadow-sm">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase">Call Us</p>
                <p className="font-bold">+1 (800) NOVUS-ENERGY</p>
              </div>
            </div>
            <div className="flex items-center gap-4 bg-gray-50 p-4 rounded-xl">
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-emerald-500 shadow-sm">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
              </div>
              <div>
                <p className="text-xs font-bold text-slate-400 uppercase">Headquarters</p>
                <p className="font-bold">100 Renewable Way, Silicon Valley, CA</p>
              </div>
            </div>
          </div>
        </div>
      )
    },
    terms: {
      title: 'Terms of Service',
      body: (
        <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 no-scrollbar">
          <p className="font-bold">1. Acceptance of Terms</p>
          <p className="text-sm">By accessing the Novus Energy platform, you agree to comply with these Terms of Service. If you do not agree, please do not use our services.</p>
          <p className="font-bold">2. Service Usage</p>
          <p className="text-sm">Our energy infrastructure services are provided subject to specific project contracts. Usage of this website is for informational and lead generation purposes.</p>
          <p className="font-bold">3. Intellectual Property</p>
          <p className="text-sm">All content, including the rotating windmill logo and Novus branding, is the exclusive property of Novus Energy Inc.</p>
          <p className="font-bold">4. Limitation of Liability</p>
          <p className="text-sm">Novus Energy is not liable for indirect damages resulting from the use of renewable systems beyond what is specified in individual warranties.</p>
        </div>
      )
    },
    privacy: {
      title: 'Privacy Policy',
      body: (
        <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 no-scrollbar">
          <p className="font-bold">1. Information Collection</p>
          <p className="text-sm">We collect name and email address when you use our lead forms to provide personalized energy consultations.</p>
          <p className="font-bold">2. Data Usage</p>
          <p className="text-sm">Your data is used solely for the purpose of communicating about energy services and is never sold to third parties.</p>
          <p className="font-bold">3. Security</p>
          <p className="text-sm">We implement industry-standard encryption to protect your personal information collected via this platform.</p>
          <p className="font-bold">4. Your Rights</p>
          <p className="text-sm">You may request deletion of your contact information at any time by contacting privacy@novus.energy.</p>
        </div>
      )
    }
  }[type];

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white w-full max-w-lg rounded-3xl p-10 shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
        <button onClick={onClose} className="absolute top-6 right-6 p-2 hover:bg-gray-100 rounded-full transition-colors">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>

        <h3 className="text-3xl font-bold mb-6 text-[#1E1E1E]">{content.title}</h3>
        <div className="text-slate-600 leading-relaxed">
          {content.body}
        </div>
        
        <button 
          onClick={onClose}
          className="w-full text-white font-bold py-4 rounded-xl shadow-lg mt-10 transition-transform active:scale-95"
          style={{ backgroundColor: COLORS.novusGreen }}
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default InfoModal;